import { useState, useRef, useEffect, useCallback } from 'react'

/**
 *
 * @returns {object} { isRunning: boolean, start: function, stop: function, seconds: number, remainingTime: {object} {h: string, m: string, s: string, hms: string} }
 *
 * This hook could be extended to have options like
 * autoStart, pause functionality, onUpdate, and much more
 */
const useTimer = () => {
  const [isRunning, setIsRunning] = useState(false)
  const [seconds, setSeconds] = useState(0)
  const [remainingTime, setRemainingTime] = useState({
    h: '00',
    m: '00',
    s: '00',
    hms: '00:00:00',
  })
  const timerRef = useRef(null)

  // function to start the timer
  const start = useCallback(
    (t) => {
      setSeconds(t)
      /**
       * start the timer
       * decrement seconds every 1s
       * update the isRunning --> true
       */
      // capturing interval id for future reference
      timerRef.current = setInterval(() => {
        // 5 --> 4 --> 3 --> ..
        setSeconds((state) => state - 1)
      }, 1000)
      setIsRunning(true)
    },
    [setSeconds, setIsRunning]
  )

  // function to top the timer
  const stop = useCallback(() => {
    // clearing interval
    clearInterval(timerRef.current)
    setIsRunning(false)
    setSeconds(0)
  }, [setIsRunning, setSeconds])

  useEffect(() => {
    // to handle condition when timer reaches 0
    if (seconds < 1) {
      stop()
    }
  }, [seconds, stop])

  useEffect(() => {
    if (isRunning) {
      const h = parseInt(seconds / 3600, 10)
      const m = parseInt(seconds / 60, 10)
      const s = parseInt(seconds % 60, 10)

      setRemainingTime({
        h,
        m,
        s,
      })
    }
  }, [seconds, isRunning])

  useEffect(() => {
    // clear interval when component unmount happens
    return () => timerRef && clearInterval(timerRef.current)
  }, [])

  return {
    isRunning,
    start,
    stop,
    seconds,
    remainingTime,
  }
}

export default useTimer
